"use client";

import { QUERY_KEYS } from "@frontend/roz-sdk-core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useSdk } from "./useSdk";

export interface UseAuthResult {
  isAuthorized: boolean;
  login: (redirectUri?: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuth = (): UseAuthResult => {
  const sdk = useSdk();
  const queryClient = useQueryClient();
  const [isAuthorized, setAuthorized] = useState(() =>
    sdk.auth.isUserAuthorized()
  );

  useEffect(() => {
    const syncAuthorized = () => {
      setAuthorized(sdk.auth.isUserAuthorized());
    };

    const onLogin = () => {
      syncAuthorized();
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user });
    };

    const onLogout = () => {
      syncAuthorized();
      queryClient.removeQueries({ queryKey: QUERY_KEYS.user, exact: true });
    };

    syncAuthorized();

    const offLogin = sdk.events.on("login", onLogin);
    const offLogout = sdk.events.on("logout", onLogout);
    const offLoadUser = sdk.events.on("loadUser", syncAuthorized);

    return () => {
      offLogin?.();
      offLogout?.();
      offLoadUser?.();
    };
  }, [sdk, queryClient]);

  return {
    isAuthorized,
    login: sdk.auth.login,
    logout: sdk.auth.logout,
  };
};
